import React, {useEffect, useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import type {BottomTabBarProps} from '@react-navigation/bottom-tabs';
import {CommonActions} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import {contextToneAndLabel} from '@/entities/session/labels';
import type {AppContext} from '@/entities/session/contexts';
import {LANG_LABELS, SUPPORTED_LANGS, setLang, type Lang} from '@/shared/config/i18n';
import {useRailStore} from '@/shared/model/rail-store';
import {Avatar} from '@/shared/ui/avatar';
import {fonts} from '@/shared/ui/fonts';
import {Icon} from '@/shared/ui/icons';
import {BrandMark} from '@/shared/ui/logo';
import {Menu, MenuLabel, MenuOption, MenuSeparator, useAnchor} from '@/shared/ui/menu';
import {StatusDot} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {layout, radii, spacing, useTheme} from '@/shared/ui/theme';
import {THEME_MODES, useThemeMode, useThemeModeStore} from '@/shared/ui/theme-mode';

/**
 * Боковая панель широкого окна — shadcn-блок `sidebar-07`, часть за частью:
 *
 *      ┌──────────────────────────┐
 *      │ [■] ООО «Пример»      ⇅  │  SidebarHeader → TeamSwitcher: чей это кабинет,
 *      │     Организация          │  и меню со всеми контекстами аккаунта
 *      ├──────────────────────────┤
 *      │  ▣  Главная              │  SidebarMenu: h-8, rounded-md, активная строка —
 *      │  ☰  Журнал               │  `bg-sidebar-accent` и полная краска
 *      │  …                       │
 *      │                          │
 *      │  ⚙  Настройки            │  NavSecondary: прижата к низу содержимого
 *      ├──────────────────────────┤
 *      │ (В) Владелец          ⇅  │  SidebarFooter → NavUser: меню с аккаунтом,
 *      │     owner@example.com    │  темой, языком и выходом
 *      └──────────────────────────┘
 *
 * ## Что изменилось
 *
 * Панель перестала быть складом кнопок. Раньше внизу стояли: чип контекста, три
 * квадратные кнопки (обновить, язык, тема), строка аккаунта и красная кнопка выхода —
 * шесть контролов в четырёх разных формах. Теперь их два, и оба — shadcn: переключатель
 * контекста наверху и меню пользователя внизу. Язык, тема и выход живут в меню
 * пользователя, где их держит любая консоль; «обновить» ушло в верхнюю полосу
 * страницы, потому что относится к странице, а не к панели.
 *
 * Кнопки «свернуть» здесь тоже нет: её работу делает `SidebarTrigger` в верхней
 * полосе — один и тот же глиф на одном и том же месте на каждом экране.
 *
 * ## Свёрнутая
 *
 * 48pt глифов: плитка контекста, значки разделов, аватар. Выбор запоминается.
 */
const styles = StyleSheet.create({
  rail: {borderRightWidth: StyleSheet.hairlineWidth},

  /* Заголовок панели — переключатель контекста. */
  header: {padding: spacing(2)},
  switcher: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    height: 48,
    paddingHorizontal: spacing(2),
    borderRadius: radii.md,
  },
  switcherCollapsed: {width: 32, height: 32, paddingHorizontal: 0, justifyContent: 'center'},
  switcherText: {flex: 1, minWidth: 0},

  content: {flex: 1, minHeight: 0},
  nav: {flexGrow: 1, paddingHorizontal: spacing(2), paddingVertical: spacing(1), gap: 2},
  navCollapsed: {alignItems: 'center'},
  spacer: {flex: 1},

  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    height: 32,
    paddingHorizontal: spacing(2),
    borderRadius: radii.md,
    overflow: 'hidden',
  },
  itemCollapsed: {width: 32, paddingHorizontal: 0, justifyContent: 'center'},
  glyph: {width: 16, alignItems: 'center', justifyContent: 'center'},
  itemLabel: {flexShrink: 1, fontSize: 14, lineHeight: 20},
  itemLabelOn: {fontFamily: fonts.sans.medium},
  itemLabelOff: {fontFamily: fonts.sans.regular},

  footer: {padding: spacing(2), borderTopWidth: StyleSheet.hairlineWidth},
  user: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    height: 48,
    paddingHorizontal: spacing(2),
    borderRadius: radii.md,
  },
  userCollapsed: {width: 32, height: 32, paddingHorizontal: 0, justifyContent: 'center'},
  userText: {flex: 1, minWidth: 0},
  tile: {alignItems: 'center', justifyContent: 'center'},
});

/** Курсор над контролом или палец на нём. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

/**
 * Переключатель контекста — shadcn `TeamSwitcher`. Плитка с маркой, имя контекста,
 * его вид под ним и шевроны справа, когда есть куда переключаться.
 */
function ContextSwitcher({collapsed}: {collapsed: boolean}) {
  const {t} = useTranslation();
  const p = useTheme();
  const contexts = useAuthStore(s => s.contexts);
  const active = useAuthStore(s => s.activeContext);
  const setActiveContext = useAuthStore(s => s.setActiveContext);
  const [open, setOpen] = useState(false);
  const {ref, anchor, measure} = useAnchor();

  if (!active) {
    return null;
  }
  const badge = contextToneAndLabel(active);
  const switchable = contexts.length > 1;

  function choose(context: AppContext) {
    setActiveContext(context);
    setOpen(false);
  }

  return (
    <>
      <Pressable
        ref={ref}
        testID="context-chip"
        accessibilityRole={switchable ? 'button' : 'text'}
        accessibilityLabel={
          switchable ? t('context.switch') : `${t(badge.kindKey)}: ${active.title}`
        }
        disabled={!switchable}
        onPress={() => measure(() => setOpen(true))}
        style={state => [
          styles.switcher,
          collapsed && styles.switcherCollapsed,
          {backgroundColor: isHot(state) && switchable ? p.chromeHover : 'transparent'},
        ]}>
        <BrandMark size={32} />
        {collapsed ? null : (
          <>
            <View style={styles.switcherText}>
              <AppText variant="subtitle" color={p.chromeForeground} numberOfLines={1}>
                {active.title}
              </AppText>
              <AppText variant="caption" numberOfLines={1}>
                {t(badge.kindKey)}
                {active.kind === 'company' ? ` · ${t(`context.role.${active.role}`)}` : ''}
              </AppText>
            </View>
            {switchable ? <Icon name="updown" size={16} color={p.chromeMuted} /> : null}
          </>
        )}
      </Pressable>

      <Menu
        visible={open}
        onClose={() => setOpen(false)}
        anchor={anchor}
        title={t('context.title')}
        minWidth={240}
        testID="context-sheet">
        <MenuLabel>{t('context.title')}</MenuLabel>
        {contexts.map(c => {
          const b = contextToneAndLabel(c);
          const isActive = active.kind === c.kind && active.companyId === c.companyId;
          return (
            <MenuOption
              key={`${c.kind}:${c.companyId}`}
              testID={`context-option-${c.kind}`}
              label={c.title}
              hint={`${t(b.kindKey)}${
                c.kind === 'company' ? ` · ${t(`context.role.${c.role}`)}` : ''
              }`}
              selected={isActive}
              leading={<StatusDot tone={b.tone} size={8} />}
              onPress={() => choose(c)}
            />
          );
        })}
      </Menu>
    </>
  );
}

/**
 * Меню пользователя — shadcn `NavUser`: аватар, имя, почта, и за шевронами — аккаунт,
 * оформление, язык, выход.
 */
function UserMenu({collapsed, onAccount}: {collapsed: boolean; onAccount: () => void}) {
  const {t, i18n} = useTranslation();
  const p = useTheme();
  const account = useAuthStore(s => s.account);
  const signOut = useAuthStore(s => s.signOut);
  const themeMode = useThemeMode();
  const setThemeMode = useThemeModeStore(s => s.setMode);
  const lang = (i18n.resolvedLanguage ?? 'ru') as Lang;
  const [open, setOpen] = useState(false);
  const {ref, anchor, measure} = useAnchor();

  if (!account) {
    return null;
  }
  const name = account.fullName || account.username;

  return (
    <>
      <Pressable
        ref={ref}
        testID="rail-account"
        accessibilityRole="button"
        accessibilityLabel={t('settings.account')}
        onPress={() => measure(() => setOpen(true))}
        style={state => [
          styles.user,
          collapsed && styles.userCollapsed,
          {backgroundColor: isHot(state) ? p.chromeHover : 'transparent'},
        ]}>
        <Avatar brand name={name} size={32} />
        {collapsed ? null : (
          <>
            <View style={styles.userText}>
              <AppText variant="subtitle" color={p.chromeForeground} numberOfLines={1}>
                {name}
              </AppText>
              <AppText variant="caption" numberOfLines={1}>
                {account.email || account.username}
              </AppText>
            </View>
            <Icon name="updown" size={16} color={p.chromeMuted} />
          </>
        )}
      </Pressable>

      <Menu
        visible={open}
        onClose={() => setOpen(false)}
        anchor={anchor}
        title={name}
        minWidth={240}
        testID="user-menu">
        <MenuOption
          testID="rail-account-open"
          icon="profile"
          label={t('settings.account')}
          hint={account.email || undefined}
          selected={false}
          onPress={() => {
            setOpen(false);
            onAccount();
          }}
        />
        <MenuSeparator />
        <MenuLabel>{t('account.theme')}</MenuLabel>
        {THEME_MODES.map(mode => (
          <MenuOption
            key={mode}
            testID={`theme-option-${mode}`}
            label={t(`account.themeMode.${mode}`)}
            selected={themeMode === mode}
            onPress={() => setThemeMode(mode)}
          />
        ))}
        <MenuSeparator />
        <MenuLabel>{t('account.language')}</MenuLabel>
        {SUPPORTED_LANGS.map(code => (
          <MenuOption
            key={code}
            testID={`language-option-${code}`}
            // Каждый язык называет себя на себе.
            label={LANG_LABELS[code]}
            selected={lang === code}
            onPress={() => {
              void setLang(code);
              setOpen(false);
            }}
          />
        ))}
        <MenuSeparator />
        <MenuOption
          testID="rail-logout"
          icon="logout"
          label={t('profile.signOut')}
          selected={false}
          danger
          onPress={() => {
            setOpen(false);
            void signOut();
          }}
        />
      </Menu>
    </>
  );
}

export function AppSideBar({state, descriptors, navigation}: BottomTabBarProps) {
  const p = useTheme();

  const collapsed = useRailStore(s => s.collapsed);
  const hydrateRail = useRailStore(s => s.hydrate);
  useEffect(() => {
    void hydrateRail();
  }, [hydrateRail]);

  /** Тот же dispatch, что и у нижней панели — нацелен на ЭТОТ навигатор. */
  const go = (name: string, params?: object) =>
    navigation.dispatch({...CommonActions.navigate(name, params), target: state.key});

  const primary = state.routes.filter(r => r.name !== 'Settings');
  const secondary = state.routes.filter(r => r.name === 'Settings');

  const renderItem = (route: (typeof state.routes)[number]) => {
    const index = state.routes.indexOf(route);
    const {options} = descriptors[route.key];
    const focused = state.index === index;
    const label = options.title ?? route.name;

    return (
      <Pressable
        key={route.key}
        testID={`rail-${route.name}`}
        accessibilityRole="button"
        accessibilityState={{selected: focused}}
        accessibilityLabel={options.tabBarAccessibilityLabel ?? label}
        onPress={() => {
          const event = navigation.emit({
            type: 'tabPress',
            target: route.key,
            canPreventDefault: true,
          });
          if (!focused && !event.defaultPrevented) {
            go(route.name, route.params);
          }
        }}
        onLongPress={() => navigation.emit({type: 'tabLongPress', target: route.key})}
        style={press => [
          styles.item,
          collapsed && styles.itemCollapsed,
          {
            backgroundColor: focused
              ? p.chromeRaised
              : isHot(press)
              ? p.chromeHover
              : 'transparent',
          },
        ]}>
        <View style={styles.glyph}>
          {options.tabBarIcon?.({
            focused,
            color: focused ? p.chromeForeground : p.chromeMuted,
            size: 16,
          })}
        </View>
        {collapsed ? null : (
          <AppText
            color={focused ? p.chromeForeground : p.chromeMuted}
            numberOfLines={1}
            style={[styles.itemLabel, focused ? styles.itemLabelOn : styles.itemLabelOff]}>
            {label}
          </AppText>
        )}
      </Pressable>
    );
  };

  return (
    <View
      testID="app-sidebar"
      style={[
        styles.rail,
        {
          width: collapsed ? layout.sidebarCollapsedWidth : layout.sidebarWidth,
          backgroundColor: p.chrome,
          borderRightColor: p.chromeBorder,
        },
      ]}>
      <View style={[styles.header, collapsed && styles.navCollapsed]}>
        <ContextSwitcher collapsed={collapsed} />
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={[styles.nav, collapsed && styles.navCollapsed]}
        showsVerticalScrollIndicator={false}>
        {primary.map(renderItem)}
        {/* «Настройки» — внизу содержимого, отдельно от того, зачем кабинет
            открывают каждый день. Это shadcn `NavSecondary` с `mt-auto`. */}
        {secondary.length > 0 ? (
          <>
            <View style={styles.spacer} />
            {secondary.map(renderItem)}
          </>
        ) : null}
      </ScrollView>

      <View
        style={[
          styles.footer,
          collapsed && styles.navCollapsed,
          {borderTopColor: p.chromeBorder},
        ]}>
        <UserMenu
          collapsed={collapsed}
          onAccount={() => go('Settings', {screen: 'Account'})}
        />
      </View>
    </View>
  );
}
